import { Box, Button, Container, Heading, Text, VStack, useToast, Flex, Collapse, useDisclosure } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import YouTube from 'react-youtube';
import { supabase } from '../../../lib/supabase';
import { useState } from 'react';
import { useCourseConfig } from '../../../hooks/useCourseConfig';

export default function ND101ACON1Landing() {
  const navigate = useNavigate();
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const courseConfig = useCourseConfig();
  const { isOpen, onToggle } = useDisclosure();

  const handleConsent = async () => {
    setIsLoading(true);
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      const { data: courseData } = await supabase
        .from('courses')
        .select('id')
        .eq('code', courseConfig.code)
        .single();

      if (!courseData) {
        throw new Error('Course not found');
      }

      const { error } = await supabase
        .from('course_learner_preferences')
        .upsert(
          { 
            user_id: user.id, 
            course_id: courseData.id,
            consent_given: true,
          },
          { 
            onConflict: 'user_id,course_id',
            ignoreDuplicates: false
          }
        );

      if (error) throw error;

      toast({
        title: 'Consent recorded',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      navigate('/preferences');
    } catch (error) {
      console.error('Error recording consent:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      toast({
        title: 'Error recording consent',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box bg="white" minH="100vh" py={12}>
      <Container maxW="4xl">
        <VStack spacing={6} textAlign="center" mb={8}>
          <Heading as="h1" size="2xl" color="gray.800">
            Rad Learning Tutor
          </Heading>
          <Text fontSize="xl" color="gray.600" maxW="2xl">
            Welcome to the first part of the Neurodiversity Learning Experience!
          </Text>
        </VStack>

        <Box 
          bg="white" 
          p={8} 
          borderRadius="xl" 
          mb={12}
          borderWidth="2px" 
          borderColor="#1AC8ED"
          boxShadow="sm"
        >
          <Heading as="h2" size="lg" color="#C200FB" mb={6}>
            Here's what happens in this activity
          </Heading>
          <Box as="ol" listStyleType="none" mb={6}>
            {[
              <Text key="watch"><Text as="span" fontWeight="bold">Watch</Text> a short Rad Learning Explainer video.</Text>,
              <Text key="consent"><Text as="span" fontWeight="bold">Consent</Text> to having your conversation with the Tutor recorded.</Text>,
              <Text key="tell"><Text as="span" fontWeight="bold">Tell</Text> the AI Tutor a bit about how you like to communicate and learn.</Text>,
              <Text key="chat">Have a <Text as="span" fontWeight="bold">chat</Text> with the AI Tutor to reflect on what you've learned.</Text>
            ].map((item, index) => (
              <Flex as="li" key={index} mb={3}>
                <Flex align="flex-start">
                  <Flex
                    align="center"
                    justify="center"
                    bg="#C200FB"
                    color="white"
                    fontWeight="bold"
                    borderRadius="full"
                    w={8}
                    h={8}
                    flexShrink={0}
                    mr={3}
                    aria-hidden="true"
                  >
                    {index + 1}
                  </Flex>
                  <Box flex="1" mt={1} lineHeight="tall">{item}</Box>
                </Flex>
              </Flex>
            ))}
          </Box>
          <Text color="gray.700" fontSize="lg" fontWeight="semibold">
            Takes about 20-30 minutes. Let's get started!
          </Text>
        </Box>

        <Box 
          bg="white"
          p={8}
          borderRadius="xl"
          borderWidth="2px"
          borderColor="#1AC8ED"
          boxShadow="sm"
          mb={12}
        >
          <Heading as="h2" size="lg" color="#C200FB" mb={6}>
            Step 1: Watch the Rad Learning Explainer Video
          </Heading>
          <Box 
            bg="gray.50"
            borderRadius="lg" 
            p={{ base: 4, md: 8 }} 
            display="flex" 
            justifyContent="center" 
            alignItems="center"
            mt={4}
            width="100%"
            overflow="hidden"
          >
            <Box 
              width="100%" 
              maxW="800px"
              position="relative"
              paddingBottom="56.25%"
              height="0"
              overflow="hidden"
            >
              <Box 
                as="div"
                position="absolute"
                top="0"
                left="0"
                width="100%"
                height="100%"
                borderRadius="lg"
                overflow="hidden"
                boxShadow="0 4px 6px rgba(0, 0, 0, 0.1)"
              >
                <YouTube
                  videoId="g_16IsJRCOE"
                  opts={{
                    width: '100%',
                    height: '100%',
                    playerVars: {
                      autoplay: 0,
                      controls: 1,
                      rel: 0,
                      showinfo: 0,
                      modestbranding: 1
                    },
                  }}
                  style={{
                    position: 'absolute',
                    top: '0',
                    left: '0',
                    width: '100%',
                    height: '100%',
                    border: 'none'
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        <Box 
          bg="white" 
          p={8} 
          borderRadius="xl" 
          borderWidth="2px"
          borderColor="#C200FB"
          boxShadow="sm"
          mb={12}
        >
          <Heading as="h2" size="lg" color="#C200FB" mb={6}>
            Step 2: Consent to having your interactions recorded
          </Heading>
          
          <Heading as="h3" size="md" color="gray.800" mb={4}>
            How does the Rad Learning Tutor Work?
          </Heading>
          <Text color="gray.700" lineHeight="tall" mb={6}>
            The Rad Learning Tutor holds the video above and some context from your organisation. It will summarise the information in this activity and ask you relevant questions to help you reflect and deepen your understanding. Before you begin it will ask you how you like to communicate and learn, so it can give you information and ask questions in the way that works best for you.
          </Text>

          <Button
            onClick={onToggle}
            variant="link"
            color="#C200FB"
            fontWeight="semibold"
            mb={4}
            _hover={{ textDecoration: 'underline' }}
          >
            {isOpen ? '▼' : '▶'} Curious to learn more?
          </Button>

          <Collapse in={isOpen} animateOpacity>
            <Box p={4} bg="gray.50" borderRadius="md" mb={6}>
              <Heading as="h4" size="sm" color="gray.800" mb={3}>
                What is it?
              </Heading>
              <Text color="gray.700" lineHeight="tall" mb={4}>
                The Rad Learning Tutor is an AI-powered chat application that helps you to embed your learning by asking questions. This will give you an opportunity to explore what you know and what you're still learning. Even though it has access to all the information in your learning materials, it's not a question-answering chatbot. Instead, it's a question-asking chatbot.
              </Text>

              <Heading as="h4" size="sm" color="gray.800" mb={3}>
                How does it work?
              </Heading>
              <Text color="gray.700" lineHeight="tall" mb={4}>
                The tutor has access to the information you're learning and uses generative AI technology to create questions and respond to your comments. However, generative AI can sometimes be mistaken, so it's always important to ask a Rad Learning Educator if there's anything that is concerning or doesn't make sense.
              </Text>

              <Heading as="h4" size="sm" color="gray.800" mb={3}>
                Why is it important?
              </Heading>
              <Text color="gray.700" lineHeight="tall" mb={4}>
                The chat gives you space to reflect on and apply what you're learning. You can do it when it works for you and in the way you communicate best. It helps you explore the ideas before the workshop, so you're ready to dive into the collaborative activities.
              </Text>
              <Text color="gray.700" lineHeight="tall">
                Your conversation also shapes the workshop. It helps us understand what your cohort already knows, what needs more exploration, and what's most relevant to your team. Then the workshop focuses on what you actually need, not generic content.
              </Text>
            </Box>
          </Collapse>

          <Box borderTop="1px" borderColor="gray.200" pt={6} mt={6}>
            <Heading as="h3" size="md" color="gray.800" mb={4}>
              Our tutor's a great listener!
            </Heading>
            <VStack align="start" width="100%" mb={6} spacing={3} px={0}>
              <Text color="gray.700" width="100%" textAlign="left" px={0}>
                By chatting, you agree that Rad Learning can record and review the conversation that you have with the Tutor.
              </Text>
              <Text color="gray.700" fontWeight="bold" width="100%" textAlign="left" px={0}>
                We only use your conversation to improve the Rad Learning Experience, so we will never share it with anyone else.
              </Text>
            </VStack>
            <Button 
              size="lg" 
              onClick={handleConsent}
              px={{ base: 4, md: 8 }}
              width={{ base: '100%', md: 'auto' }}
              height="auto"
              py={3}
              whiteSpace="normal"
              isLoading={isLoading}
            >
              I consent to recording this conversation
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
